import React, { useState } from "react";
import usePack from "../../../../hooks/shared/packs/usePack";
import type { PackType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { BnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/Popover";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Text from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import ItemList from "../../common/ItemList";
import AccentColours from "../../common/extras/AccentColours";
import Extra from "../../common/extras/Extra";
import OptionalPackEquipment from "./OptionalPackEquipment";
import * as Styles from "./styles/Pack";
import {
    ToggleIconWrapper,
    IconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Price from "../../common/price/Price";
import * as Image from "../../../../../../../common-deprecated/styles/toyota/Image";
import { trackCustomizeConfigItem, trackPackViewFeatures } from "../../../../utils/tracking";
import Icon, { IconChevronDown } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import { SrOnly } from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { useUniqueId } from "../../../../../../../common-deprecated/hooks";

type PropsType = {
    pack: PackType;
    showImage: boolean;
    packIndex: number;
    isSinglePack?: boolean; // This will hide the actions and show an opened state of a pack
};

const Pack = (props: PropsType): JSX.Element | null => {
    const uniqueId = useUniqueId();

    const { pack, showImage, packIndex, isSinglePack } = props;
    const [isOpen, setOpen] = useState<boolean>(!!isSinglePack);
    const packProps = usePack(pack);

    const [includedPack, disabledPack] = useBnbOptionalLabel(["includedPack", "disabledPack"]);

    if (!packProps) return null;

    const { actions, packContent } = packProps;
    const { selectPack, deselectPack, selectPrimaryAccentColour } = actions;
    const {
        selected,
        enabled,
        price,
        primaryAccentColour,
        primaryAccentColours,
        colours,
        upholsteries,
        wheels,
        renderedEquipment,
        totalPackContent,
        optionalEquipment,
    } = packContent;

    const { id, name, image, footNote, description } = pack;
    const hasImage = showImage && image;

    return (
        <Styles.Wrapper packIndex={packIndex} selected={selected} isSinglePack={isSinglePack} disableBorder={!enabled}>
            <Styles.Header hasBackground={showImage && !!image} hasImage={!!hasImage}>
                {hasImage && <Image.Fluid src={image!} alt={pack.name} />}
                <Styles.TitleWrapper hasImage={!!hasImage}>
                    <Styles.Title as="h3" id={`${uniqueId}-header`}>
                        <span>{name}</span>
                        {description && <Popover showOnClick content={description} useHTML />}
                    </Styles.Title>
                    <Price price={price} financeItemId={id} />
                    {primaryAccentColours.length > 1 && (
                        <AccentColours
                            selectedColour={primaryAccentColour}
                            colours={primaryAccentColours}
                            selectColour={selectPrimaryAccentColour}
                            enabled={enabled && selected}
                        />
                    )}
                </Styles.TitleWrapper>
            </Styles.Header>

            {!!footNote && <Styles.FootNote>{footNote}</Styles.FootNote>}

            {!isSinglePack && (
                <Styles.PackAction isActive={isOpen} hasFootnote={!!footNote}>
                    <Button.Link
                        hasIcon
                        hasIconRight
                        large
                        onClick={() => {
                            if (!isOpen) trackPackViewFeatures();
                            setOpen(!isOpen);
                        }}
                        aria-expanded={isOpen}
                        aria-controls={uniqueId}
                    >
                        <BnbLabel label="packsShowFeatures" params={{ count: totalPackContent }} />
                        <ToggleIconWrapper isDown={isOpen}>
                            <IconChevronDown />
                        </ToggleIconWrapper>
                    </Button.Link>

                    <Button.Checkbox
                        type="button"
                        selected={selected}
                        disabled={!enabled}
                        aria-pressed={selected}
                        onClick={
                            enabled
                                ? () => {
                                      if (selected) {
                                          deselectPack();
                                          trackCustomizeConfigItem(NavStep.Customize, "remove-extra");
                                      } else {
                                          selectPack();
                                          setOpen(true);
                                          trackCustomizeConfigItem(NavStep.Customize, "add-extra");
                                      }
                                  }
                                : undefined
                        }
                        aria-expanded={isOpen}
                        aria-controls={uniqueId}
                    >
                        {selected && !enabled && includedPack && <Text.Button>{includedPack}</Text.Button>}
                        {!selected && !enabled && disabledPack && <Text.Button>{disabledPack}</Text.Button>}
                        <SrOnly>
                            {selected ? (
                                <BnbLabel label="bnbA11yButtonPackDeselect" />
                            ) : (
                                <BnbLabel label="bnbA11yButtonPackSelect" />
                            )}
                        </SrOnly>
                        <IconWrapper>
                            <Icon variant={selected ? "close" : "plus"} />
                        </IconWrapper>
                    </Button.Checkbox>
                </Styles.PackAction>
            )}
            {isOpen && (
                <>
                    <Styles.ContentWrapper
                        isOpen={isOpen}
                        id={uniqueId}
                        role="region"
                        aria-labelledby={`${uniqueId}-header`}
                    >
                        {colours.length > 0 && <ItemList activeItemType="colour" items={colours} hasBorder />}
                        {wheels.length > 1 && <ItemList activeItemType="wheel" items={wheels} hasBorder />}
                        {upholsteries.length > 1 && (
                            <ItemList activeItemType="upholstery" items={upholsteries} hasBorder />
                        )}

                        {renderedEquipment.length > 0 && (
                            <Styles.Grid>
                                {renderedEquipment.map((extra) => (
                                    <Extra key={extra.id} extra={extra} enabled />
                                ))}
                            </Styles.Grid>
                        )}
                    </Styles.ContentWrapper>

                    {optionalEquipment.length > 0 && (
                        <OptionalPackEquipment
                            equipment={packContent.optionalEquipment}
                            selectedEquipment={packContent.selectedEquipment}
                            equipmentSelect={actions.equipmentSelect}
                            equipmentDeselect={actions.equipmentDeselect}
                            secondaryAccentColour={packContent.secondaryAccentColour}
                            secondaryAccentColours={packContent.secondaryAccentColours}
                            selectSecondaryAccentColour={actions.selectSecondaryAccentColour}
                            enabled={enabled && selected}
                        />
                    )}
                </>
            )}
        </Styles.Wrapper>
    );
};

export default Pack;
